import { useCallback, useState } from 'react';
import { Canvas, IText } from 'fabric';
import { usePieceDispatch } from '../../model/piece-context';
import { useSelectedLayer, useSelectedLayerId } from '../../selection/hooks/use-selected-layer';
import type { Fill } from '../../shared/utils/fill';
import { readSelectionStyle, selectionRange, writeSelectionStyle } from '../utils/selection-style';
import { useOnSelectionChange } from './use-on-selection-change';

// The picker only speaks solid colours, so a gradient shows its first stop.
const toColor = (fill: Fill, fallback: string): string =>
  fill.kind === 'solid' ? fill.color : (fill.stops[0]?.color ?? fallback);

export const useTextFill = (
  textObject: IText,
  canvas: Canvas,
  defaultValue: string
): [string, (color: string) => void] => {
  const dispatch = usePieceDispatch();
  const layerId = useSelectedLayerId();
  const layer = useSelectedLayer();
  const fill = layer?.kind === 'text' ? layer.content.fill : null;

  const [selected, setSelected] = useState<string | null>(null);

  const read = useCallback(() => {
    // Per char it's always a plain colour; anything else isn't ours to show.
    const value = readSelectionStyle(textObject)?.fill;
    setSelected(typeof value === 'string' ? value : null);
  }, [textObject]);
  useOnSelectionChange(textObject, read);

  const apply = useCallback(
    (color: string) => {
      if (!layerId) return;

      if (!selectionRange(textObject)) {
        dispatch({ type: 'patchLayerContent', id: layerId, patch: { fill: { kind: 'solid', color } } });
        return;
      }

      setSelected(color);
      const styles = writeSelectionStyle(textObject, 'fill', color);
      canvas.requestRenderAll();
      dispatch({ type: 'patchLayerContent', id: layerId, patch: { styles } });
    },
    [dispatch, layerId, textObject, canvas]
  );

  return [selected ?? (fill ? toColor(fill, defaultValue) : defaultValue), apply];
};
